/**
 * One plan card (AR 3:4). Every number from `src/lib/pricing.ts`, every word
 * from `pricing.*`. The recommended card wears the shared `.spotlight-surface`
 * (same accent shading + spotlight as the Last CTA and the Pro gate).
 */
import { ArrowRight, Check, Loader2, Minus, Plus } from 'lucide-react'
import { localizedPath, useTranslation } from '../../lib/i18n'
import type { PlanCtaAction } from '../../lib/pricing-cta'
import {
  PLANS,
  TEAM_MAX_SEATS,
  extraSeatPricePerMonth,
  formatPrice,
  pricePerMonth,
  teamPricePerMonth,
} from '../../lib/pricing'
import type { BillingInterval, PlanId } from '../../lib/pricing'
import { MoneyBackGuarantee } from './money-back-guarantee'

export type PlanCardProps = {
  planId: PlanId
  interval: BillingInterval
  action: PlanCtaAction
  /** True while THIS card's checkout/portal request is in flight. */
  pending: boolean
  teamSeats: number
  onTeamSeatsChange: (seats: number) => void
  /** Click on a button CTA (waitlist, checkout, manage, switch). */
  onAction: () => void
  /** href for link CTAs (start_free, signup). */
  signupHref: string
  onSignupClick: () => void
  headingLevel: 'h2' | 'h3'
}

export function PlanCard(props: PlanCardProps) {
  const { locale, t, dict } = useTranslation()
  const { planId, interval, teamSeats } = props
  const plan = PLANS[planId]
  const isTeam = planId === 'team'
  const perMonth = isTeam
    ? teamPricePerMonth(teamSeats, interval)
    : pricePerMonth(planId, interval)
  const Heading = props.headingLevel

  const billing =
    planId === 'free'
      ? t('pricing.freeBilling')
      : interval === 'year'
        ? t('pricing.billedYearly', {
            total: formatPrice(perMonth * 12, locale),
          })
        : t('pricing.billedMonthly')

  return (
    <article
      className={
        plan.recommended
          ? 'plan-card is-recommended spotlight-surface'
          : 'plan-card'
      }
      data-plan={planId}
      aria-labelledby={`plan-${planId}-name`}
    >
      <header className="plan-card-head">
        <div className="plan-card-title">
          <Heading id={`plan-${planId}-name`}>
            {dict.pricing.plans[planId].name}
          </Heading>
          {plan.recommended ? (
            <span className="plan-flag">{t('pricing.recommended')}</span>
          ) : null}
        </div>
        <p className="plan-tagline">{dict.pricing.plans[planId].tagline}</p>
      </header>

      <div className="plan-price">
        {/* Keyed so the amount re-animates when the interval flips. */}
        <strong key={`${interval}-${perMonth}`} className="plan-price-amount">
          {formatPrice(perMonth, locale)}
        </strong>
        <span className="plan-price-unit">{t('pricing.perMonth')}</span>
      </div>
      <p className="plan-billing" data-testid={`plan-billing-${planId}`}>
        {billing}
      </p>

      {isTeam ? (
        <div className="plan-seats">
          <span className="plan-seats-label" id="plan-team-seats-label">
            {t('pricing.seatsLabel')}
          </span>
          <div
            className="plan-seats-stepper"
            role="group"
            aria-labelledby="plan-team-seats-label"
          >
            <button
              type="button"
              aria-label={t('pricing.seatsDecrease')}
              disabled={teamSeats <= plan.seatsIncluded}
              onClick={() => props.onTeamSeatsChange(teamSeats - 1)}
            >
              <Minus size={14} aria-hidden="true" />
            </button>
            <output aria-live="polite">{teamSeats}</output>
            <button
              type="button"
              aria-label={t('pricing.seatsIncrease')}
              disabled={teamSeats >= TEAM_MAX_SEATS}
              onClick={() => props.onTeamSeatsChange(teamSeats + 1)}
            >
              <Plus size={14} aria-hidden="true" />
            </button>
          </div>
          <small className="plan-seats-note">
            {t('pricing.seatsIncluded', { count: plan.seatsIncluded })}
            {' · '}
            {t('pricing.extraSeat', {
              price: formatPrice(extraSeatPricePerMonth(), locale),
            })}
          </small>
        </div>
      ) : null}

      <ul className="plan-features">
        {plan.featureKeys.map((key) => (
          <li key={key}>
            <span className="plan-check" aria-hidden="true">
              <Check size={13} strokeWidth={3} />
            </span>
            {dict.pricing.features[key]}
          </li>
        ))}
      </ul>

      <div className="plan-cta">
        <PlanCta {...props} />
        {planId === 'free' ? (
          <p className="guarantee-inline is-muted">{t('pricing.freeNote')}</p>
        ) : (
          <MoneyBackGuarantee variant="inline" />
        )}
      </div>
    </article>
  )
}

function PlanCta(props: PlanCardProps) {
  const { locale, t } = useTranslation()
  const { planId, action, pending } = props
  const buyLabel =
    planId === 'team' ? t('pricing.ctaGetTeam') : t('pricing.ctaGetPro')
  const className = 'primary-button plan-cta-button'

  switch (action) {
    case 'start_free':
      return (
        <a
          className={className}
          href={localizedPath('course', locale, { courseSlug: 'foundations' })}
          onClick={props.onSignupClick}
        >
          {t('pricing.ctaFree')}
          <ArrowRight size={17} aria-hidden="true" />
        </a>
      )
    case 'signup':
      return (
        <a
          className={className}
          href={props.signupHref}
          onClick={props.onSignupClick}
        >
          {buyLabel}
          <ArrowRight size={17} aria-hidden="true" />
        </a>
      )
    case 'included':
      return (
        <button className={className} type="button" disabled>
          {t('pricing.ctaIncluded')}
        </button>
      )
    case 'waitlist':
    case 'checkout':
    case 'manage':
    case 'switch': {
      const label =
        action === 'waitlist'
          ? t('pricing.ctaWaitlist')
          : action === 'manage'
            ? t('pricing.ctaManage')
            : action === 'switch'
              ? t('pricing.ctaSwitch')
              : buyLabel
      return (
        <button
          className={className}
          type="button"
          onClick={props.onAction}
          disabled={pending}
          aria-busy={pending || undefined}
        >
          {pending ? (
            <>
              <Loader2 size={17} className="spin" aria-hidden="true" />
              {t('pricing.ctaRedirecting')}
            </>
          ) : (
            <>
              {label}
              <ArrowRight size={17} aria-hidden="true" />
            </>
          )}
        </button>
      )
    }
  }
}
